import { Box, Flex, Select, Text, Title, useMantineTheme } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import MetadataDocuments from "src/components/MetadataDocuments";
import { useDatasources } from "src/hooks/useDatasources";
import { type DatasourceIngestionStatus, useIngestionStatus } from "src/hooks/useIngestionStatus";

export const DocumentsRoute = ({
    index,
    setSelectedIndex,
}: {
    index: string;
    setSelectedIndex: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [filteredDocumentCount, setFilteredDocumentCount] = useState<number | undefined>(
        undefined,
    );
    const [storedUserDatasources, _] = useLocalStorage<string[]>("userDatasources", []);
    const { data: chatVectorIndex = [], isSuccess } = useDatasources();
    const getDisplayLabel = useCallback((label: string, datasourceLabel: string) => {
        const normalize = (s: string) => s.toLowerCase().trim();
        return normalize(label) !== normalize(datasourceLabel)
            ? `${label} (${datasourceLabel})`
            : label;
    }, []);

    const titles = useMemo(
        () =>
            Object.fromEntries(
                chatVectorIndex.map((o) => [o.id, getDisplayLabel(o.label, o.datasource_label)]),
            ) as Record<string, string>,
        [chatVectorIndex, getDisplayLabel],
    );

    const normalizeString = useCallback(
        (str: string) => str.toLowerCase().replace(/[_\s]/g, ""),
        [],
    );

    const getAvailableIndicesInUserRoles = useCallback(() => {
        return chatVectorIndex.filter((vectorIndex) =>
            storedUserDatasources.some(
                (userDs) =>
                    normalizeString(userDs).includes(
                        normalizeString(vectorIndex.datasource_label),
                    ) ||
                    normalizeString(vectorIndex.datasource_label).includes(
                        normalizeString(userDs),
                    ) ||
                    normalizeString(userDs).includes(normalizeString(vectorIndex.label)) ||
                    normalizeString(vectorIndex.label).includes(normalizeString(userDs)) ||
                    normalizeString(userDs) === normalizeString(vectorIndex.datasource_id),
            ),
        );
    }, [chatVectorIndex, storedUserDatasources, normalizeString]);

    useEffect(() => {
        if (window.location.hash === "#/documents") navigate({ to: `/documents?tab=${index}` });
    }, [index, navigate]);

    useEffect(() => {
        if (!isSuccess || chatVectorIndex.length === 0) return;
        const exists = chatVectorIndex.some((ds) => ds.id === index);
        if (!exists) {
            const available = getAvailableIndicesInUserRoles();
            const firstId = (available.length > 0 ? available[0] : chatVectorIndex[0]).id;
            setSelectedIndex(firstId);
            navigate({ to: `/documents?tab=${firstId}` });
        }
    }, [
        isSuccess,
        chatVectorIndex,
        index,
        navigate,
        setSelectedIndex,
        getAvailableIndicesInUserRoles,
    ]);

    const { data: ingestionStatus } = useIngestionStatus();

    const currentDatasource = chatVectorIndex.find((ds) => ds.id === index);
    const ingestionStatusForDatasource = Array.isArray(ingestionStatus?.data_sources)
        ? ingestionStatus?.data_sources.find(
              (value) => value.id === (currentDatasource?.datasource_id ?? index),
          )
        : undefined;

    const formatDocumentCount = (count: number): string => {
        return count.toLocaleString();
    };

    const ingestionStatusText = (
        status: DatasourceIngestionStatus | undefined,
    ): string | undefined => {
        if (!status) return undefined;

        const isFiltered = filteredDocumentCount !== undefined;

        if (isFiltered) {
            const count = filteredDocumentCount;
            return count > 0
                ? `Showing (${formatDocumentCount(count)}) ${count === 1 ? "document" : "documents"}`
                : "No documents found";
        }

        return status.total_count > 0
            ? `${status.ingestion_status} (${status.total_count} documents)`
            : `${status.ingestion_status}`;
    };

    return (
        <Flex p="xl" direction="column" gap="md">
            <Flex justify="space-between">
                <Flex align={"baseline"} gap={"xs"} wrap={"wrap"}>
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        {titles[index]}
                    </Title>
                    {ingestionStatusForDatasource && (
                        <Text size="sm" fw={600} color="purple.7">
                            {ingestionStatusText(ingestionStatusForDatasource)}
                        </Text>
                    )}
                </Flex>
                <Box mt="xs" maw={300}>
                    <Select
                        id="data-sources"
                        value={index}
                        onChange={async (value: string | null) => {
                            if (value) {
                                setSelectedIndex(value);
                                navigate({ to: `/documents?tab=${value}` });
                                setFilterResetKey((prev) => prev + 1);
                            }
                        }}
                        data={getAvailableIndicesInUserRoles().map((item) => ({
                            label: getDisplayLabel(item.label, item.datasource_label),
                            value: item.id,
                        }))}
                        placeholder="Select a data source"
                        nothingFoundMessage="No data sources found"
                        styles={{
                            input: {
                                fontWeight: "bold",
                                color: theme.colors.purple[7],
                            },
                            dropdown: { maxHeight: 200, overflowY: "auto" },
                        }}
                        withScrollArea={false}
                        size="sm"
                        w={300}
                    />
                </Box>
            </Flex>
            <Box>
                <MetadataDocuments
                    index={index}
                    key={filterResetKey}
                    onFilteredCountChange={setFilteredDocumentCount}
                />
            </Box>
        </Flex>
    );
};
